import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';

import { getDocumentName, getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType, GenericStatusId } from '@e-socna/data-layer';

import { useGraphQLMutation } from '../../hooks/useGraphQLMutation.hook';
import { SpeedDial } from '../../components/SpeedDial';
import { RightDrawer } from '../../components/RightDrawer';
import { Admin as AdminCmp } from '../../components/Admin';
import {
  PATH_COST_ADMIN,
  PATH_COST_ADMIN_EDIT,
} from '../../app/constants/paths.constant';
import { GenericAdminForm } from '../../forms/GenericAdmin';
import { memoForm2ApiMapper } from '../../forms/MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { useMemos } from '../../forms/MemoBeforeSubmit/hooks/useMemos.hook';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { CostListCmp_AllCosts_QueryDocument } from '../../components/CostList/hooks/useFindAllCostsQuery.hook';
import type { GenericAdminFormValues } from '../../forms/GenericAdmin';

import { CostPage_OneCost_QueryDocument } from './hooks/useFindOneCostQuery.hook';

export const CostPage_Admin_Cost_Fragment = graphql(`
  fragment CostPage_Admin_Cost_Fragment on Cost {
    _id
    statusId
    createdAt
    creatorId
    updatedAt
    updaterId
  }
`);

export const CostPage_Admin_Cost_Foapu_Mutation = graphql(`
  mutation CostPage_Admin_Cost_Foapu(
    $filter: IdFilterInput!
    $update: CostPartialInput!
    $versionData: VersionDataInput
  ) {
    findOneAndPartialUpdateCost(
      filter: $filter
      update: $update
      versionData: $versionData
    ) {
      _id
    }
  }
`);

interface Props {
  readonly cost: FragmentType<typeof CostPage_Admin_Cost_Fragment>;
}

export const Admin = ({ cost: pCost }: Readonly<Props>) => {
  const cost = getFragmentData(CostPage_Admin_Cost_Fragment, pCost);

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_COST_ADMIN,
      edit: PATH_COST_ADMIN_EDIT,
    },
    { id: cost._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  const { enqueueSnackbar } = useSnackbar();

  const { clearMemos, memos, setMemos } = useMemos();

  // mutations
  const { mutate: foapuCostMutation } = useGraphQLMutation(
    CostPage_Admin_Cost_Foapu_Mutation,
  );

  // on success
  const queryClient = useQueryClient();

  const mutate = useCallback(
    (
      values: GenericAdminFormValues,
      handleError: (error: unknown) => unknown,
      handleSuccess: () => unknown,
    ) => {
      const onSuccess = () => {
        leaveEditionMode();
        clearMemos();
        enqueueSnackbar('Modification enregistrée.');
        handleSuccess();
        void queryClient.invalidateQueries({
          queryKey: [
            getDocumentName(CostPage_OneCost_QueryDocument),
            { id: cost._id },
          ],
        });
        void queryClient.invalidateQueries({
          queryKey: [getDocumentName(CostListCmp_AllCosts_QueryDocument)],
        });
      };

      foapuCostMutation(
        {
          filter: {
            _id: cost._id,
          },
          update: values,
          versionData: memoForm2ApiMapper(memos),
        },
        {
          onError: handleError,
          onSuccess,
        },
      );
    },
    [
      clearMemos,
      cost._id,
      enqueueSnackbar,
      foapuCostMutation,
      leaveEditionMode,
      memos,
      queryClient,
    ],
  );

  return (
    <>
      <AdminCmp
        createdAt={cost.createdAt}
        creatorId={cost.creatorId}
        id={cost._id}
        statusId={cost.statusId}
        updatedAt={cost.updatedAt}
        updaterId={cost.updaterId}
      />
      <SpeedDial
        actions={[
          {
            id: 'costAdmin',
            icon: <DisplaySettingsIcon />,
            name: 'Administrer',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur l'Administration du Coût"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <GenericAdminForm
          clearMemos={clearMemos}
          memos={memos}
          onCancel={leaveEditionMode}
          onSubmit={mutate}
          setMemos={setMemos}
          statusId={cost.statusId as GenericStatusId}
        />
      </RightDrawer>
    </>
  );
};
